import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

function getDeviceId() {
  let id = localStorage.getItem("nx_device_id");
  if (!id) {
    id = (crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36));
    localStorage.setItem("nx_device_id", id);
  }
  return id;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      console.warn("[auth] fetchMe failed", err?.response?.status || err?.message);
      setUser(null);
      localStorage.removeItem("nx_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth (hash contains session_id), let AuthCallback handle it
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    if (localStorage.getItem("nx_token") || document.cookie.includes("session_token")) {
      fetchMe();
    } else {
      setLoading(false);
    }
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password, device_name: navigator.userAgent.slice(0, 60), device_id: getDeviceId() });
    localStorage.setItem("nx_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const referral_code = localStorage.getItem("nx_ref") || null;
    const { data } = await api.post("/auth/register", { email, password, name, device_name: navigator.userAgent.slice(0, 60), device_id: getDeviceId(), referral_code });
    localStorage.setItem("nx_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("[auth] logout API call failed (proceeding with local cleanup)", err?.message);
    }
    localStorage.removeItem("nx_token");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh: fetchMe, setUser }),
    [user, loading, login, register, logout, fetchMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
